// PortalItemsCard.js - Kort på patientens Översikt: skickade formulär + inkomna svar
import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../apiClient';
import './PatientPortal.css';

const STATUS_LABEL = {
  sent: 'Skickat',
  opened: 'Öppnat',
  completed: 'Besvarat',
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('sv-SE') + ' ' +
    d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
};

/** EQ-5D-profil ("12345") + VAS ur råsvaren — fallback för svar som lagrats
 *  innan poängberäkningen fanns (nya svar har item.response.score). */
const eq5dSummary = (item) => {
  if (item.form_ref !== 'eq5d' || !item.response?.answers) return null;
  const a = item.response.answers;
  const dims = ['mobility', 'selfcare', 'activities', 'pain', 'anxiety'];
  if (!dims.every(d => a[d] !== undefined && a[d] !== null)) return null;
  return { profile: dims.map(d => a[d]).join(''), vas: a.vas };
};

const fmtNum = (n) => (n === null || n === undefined ? '–' : String(n));

const METHOD_LABELS = { sum: 'Summa', mean: 'Medelvärde', percent_of_max: 'Procent av max' };

/** Uträknad poäng (Journal-intern, från mallens scoring) — visas bara för personal. */
const ScoreSummary = ({ score }) => {
  if (!score || !score.method || score.method === 'none') return null;
  if (score.method === 'eq5d') {
    return (
      <div className="portal-score">
        <span className="portal-score-label"><i className="fas fa-calculator"></i> {score.label || 'EQ-5D-5L'}</span>
        <span><strong>Profil:</strong> {score.profile || '–'}</span>
        <span><strong>VAS:</strong> {fmtNum(score.vas)}</span>
      </div>
    );
  }
  const label = score.label || METHOD_LABELS[score.method] || 'Poäng';
  if (!score.answered) {
    return (
      <div className="portal-score">
        <span className="portal-score-label"><i className="fas fa-calculator"></i> {label}</span>
        <span className="portal-item-meta">Inga poängbara svar</span>
      </div>
    );
  }
  let main;
  if (score.method === 'percent_of_max') {
    main = <span><strong>{fmtNum(score.percent)} %</strong> <span className="portal-item-meta">({fmtNum(score.total)} av {fmtNum(score.max)})</span></span>;
  } else if (score.method === 'mean') {
    main = <span><strong>{fmtNum(score.mean)}</strong> <span className="portal-item-meta">(medel av {score.answered} frågor)</span></span>;
  } else {
    main = <span><strong>{fmtNum(score.total)}</strong> <span className="portal-item-meta">av {fmtNum(score.max)}{score.percent !== undefined ? ` · ${fmtNum(score.percent)} %` : ''}</span></span>;
  }
  return (
    <div className="portal-score">
      <span className="portal-score-label"><i className="fas fa-calculator"></i> {label}</span>
      {main}
      {score.domains && Object.keys(score.domains).length > 0 && (
        <span className="portal-score-domains">
          {Object.entries(score.domains).map(([name, d]) => (
            <span key={name} className="portal-score-domain">
              {name}: {score.method === 'mean' ? fmtNum(d.mean) : `${fmtNum(d.total)}/${fmtNum(d.max)}`}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

/** Svarstext för en fråga: option-etikett för single_choice, annars råvärdet. */
const answerLabel = (question, value) => {
  if (value === undefined || value === null || value === '') return '-';
  if (question?.type === 'single_choice') {
    const opt = (question.options || []).find(o => String(o.value) === String(value));
    return opt ? `${value} – ${opt.label}` : String(value);
  }
  return String(value);
};

const PortalItemsCard = ({ patientId, refreshKey, onSendClick }) => {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      setError(null);
      // Frågorna följer med per form_ref (även för inaktiverade mallar) så att
      // gamla svar alltid visas med frågetext och alternativetiketter.
      const itemsResp = await apiClient.get('/api/patient-portal/items', { params: { patient_id: patientId } });
      setItems(itemsResp.data.items || []);
      setTemplates(itemsResp.data.forms || {});
    } catch (err) {
      console.error('Error loading portal items:', err);
      setError(err.response?.data?.error || 'Kunde inte hämta skickade formulär');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="overview-card wide portal-card">
      <h4>
        <i className="fas fa-clipboard-list"></i> Patientportal – formulär
        <button className="portal-card-action" onClick={onSendClick} title="Skicka ett formulär till patienten">
          <i className="fas fa-paper-plane"></i> Skicka formulär
        </button>
        <button className="portal-card-refresh" onClick={load} title="Uppdatera" disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
        </button>
      </h4>

      {error && <div className="portal-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {!error && !loading && items.length === 0 && (
        <p className="portal-empty">Inga formulär skickade ännu.</p>
      )}

      {items.length > 0 && (
        <div className="portal-items">
          {items.map(item => {
            const summary = eq5dSummary(item);
            const questions = templates[item.form_ref]?.questions || [];
            const isOpen = !!expanded[item.item_id];
            return (
              <div key={item.item_id} className={`portal-item portal-state-${item.status}`}>
                <div className="portal-item-row">
                  <div className="portal-item-main">
                    <span className="portal-item-title">{item.title}</span>
                    <span className="portal-item-meta">
                      Skickat {formatDateTime(item.created_at)}
                      {item.created_by_name ? ` av ${item.created_by_name}` : ''}
                      {item.notified_via && item.notified_via !== 'none' && (
                        <> · notis via {item.notified_via === 'sms' ? 'SMS' : 'e-post'}</>
                      )}
                    </span>
                    {item.notify_error && (
                      <span className="portal-item-warning">
                        <i className="fas fa-exclamation-triangle"></i> Notis misslyckades: {item.notify_error}
                      </span>
                    )}
                  </div>
                  <div className="portal-item-side">
                    <span className={`portal-status portal-state-${item.status}`}>
                      {STATUS_LABEL[item.status] || item.status}
                    </span>
                    {item.status === 'completed' && (
                      <span className="portal-item-meta">{formatDateTime(item.completed_at)}</span>
                    )}
                    {item.status === 'opened' && (
                      <span className="portal-item-meta">{formatDateTime(item.opened_at)}</span>
                    )}
                  </div>
                </div>

                {item.status === 'completed' && item.response && (
                  <div className="portal-answers">
                    {item.response.score ? (
                      <ScoreSummary score={item.response.score} />
                    ) : summary && (
                      <div className="portal-eq5d-summary">
                        <span><strong>EQ-5D-profil:</strong> {summary.profile}</span>
                        <span><strong>VAS:</strong> {summary.vas}</span>
                      </div>
                    )}
                    <button className="portal-toggle" onClick={() => toggle(item.item_id)}>
                      <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>{' '}
                      {isOpen ? 'Dölj svar' : 'Visa svar'}
                    </button>
                    {isOpen && (
                      <table className="portal-answers-table">
                        <tbody>
                          {(questions.length ? questions : Object.keys(item.response.answers || {}).map(id => ({ id, label: id })))
                            .map(q => (
                              <tr key={q.id}>
                                <td className="q">{q.label}</td>
                                <td className="a">{answerLabel(q, item.response.answers?.[q.id])}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PortalItemsCard;
